import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import type { FunctionComponent, ReactElement, ReactNode } from "react";
import { cn } from "./cn";
import { POPUP_TRANSITION, POPUP_Z_INDEX, toPositioning, type Placement } from "./popup";

export interface TooltipProps {
  /** Tooltip content; the tooltip is disabled when empty (the child stays mounted either way). */
  title?: ReactNode;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hover delay in seconds (antd `mouseEnterDelay`). */
  mouseEnterDelay?: number;
  /** Popup class. */
  className?: string;
  /** The trigger — must accept a ref and spread props. */
  children: ReactElement;
}

/** Hover/focus label over a single child (Base UI `Tooltip`). */
export const Tooltip: FunctionComponent<TooltipProps> = ({
  title,
  placement = "top",
  open,
  onOpenChange,
  mouseEnterDelay = 0.1,
  className,
  children,
}) => {
  const empty = title === undefined || title === null || title === "" || title === false;
  const { side, align } = toPositioning(placement);

  return (
    <BaseTooltip.Root
      disabled={empty}
      open={open}
      onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
    >
      <BaseTooltip.Trigger delay={mouseEnterDelay * 1000} closeDelay={100} render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} align={align} sideOffset={6} className={POPUP_Z_INDEX}>
          <BaseTooltip.Popup
            className={cn(
              "max-w-[250px] rounded-md bg-noa-hover px-2 py-1.5 text-sm break-words text-noa-muted shadow-md shadow-noa-shadow",
              POPUP_TRANSITION,
              className
            )}
          >
            {title}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};
